import { Download, FileText, Image as ImageIcon, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Receive } from "@/types/general";
import { cn } from "@helpers/utils";
import BaseMaterialIconButton from "@reusables/BaseMaterialIconButton";

export type ReceiptDocumentItem =
    | {
          type: "local";
          key: string;
          fileName: string;
          size: number;
          mimeType?: string;
          file: File;
          index: number;
      }
    | {
          type: "remote";
          key: string;
          fileName: string;
          size: number;
          mimeType?: string;
          attachment: Receive.Components.Attachment;
      };

type FileRowProps = {
    file: ReceiptDocumentItem;
    deleteDisabled?: boolean;
    deleteDisabledReason?: string;
    downloadDisabled?: boolean;
    onDownload: (file: ReceiptDocumentItem) => void;
    onDelete?: (file: ReceiptDocumentItem) => void;
};

export default function FileRow({
    file,
    deleteDisabled,
    deleteDisabledReason,
    downloadDisabled,
    onDownload,
    onDelete,
}: FileRowProps) {
    const { t } = useTranslation("", { keyPrefix: "receive" });
    const isImage = isImageReceiptDocument(file);
    const Icon = isImage ? ImageIcon : FileText;

    const deleteButton = onDelete ? (
        <BaseMaterialIconButton
            type="button"
            style="dim"
            size="small"
            icon={<Trash2 size={16} />}
            tip={deleteDisabled ? deleteDisabledReason : t("documents.actions.delete")}
            tooltipPlacement="top"
            aria-label={t("documents.actions.deleteFile", { file: file.fileName })}
            disabled={deleteDisabled}
            onClick={(event) => {
                event.stopPropagation();
                onDelete(file);
            }}
        />
    ) : null;

    return (
        <div className="flex items-center gap-[12px] rounded-[6px] border border-solid border-[#E9E7EC] bg-white px-[12px] py-[10px]">
            <div
                className={cn(
                    "flex h-[32px] w-[32px] shrink-0 items-center justify-center rounded-[6px]",
                    isImage ? "bg-[#EDF7ED] text-[#427A49]" : "bg-[#F1F0FF] text-accent"
                )}
            >
                <Icon size={18} />
            </div>

            <div className="min-w-0 grow">
                <div className="truncate text-sm font-medium text-accent">{file.fileName}</div>
                <div className="text-xs text-gray-500">{formatFileSize(file.size)}</div>
            </div>

            <div className="flex shrink-0 items-center gap-[8px]">
                <BaseMaterialIconButton
                    type="button"
                    style="dim"
                    size="small"
                    icon={<Download size={16} />}
                    tip={t("documents.actions.download")}
                    tooltipPlacement="top"
                    aria-label={t("documents.actions.downloadFile", { file: file.fileName })}
                    disabled={downloadDisabled}
                    onClick={(event) => {
                        event.stopPropagation();
                        onDownload(file);
                    }}
                />

                {deleteButton}
            </div>
        </div>
    );
}

function isImageReceiptDocument(file: ReceiptDocumentItem) {
    const mimeType = file.mimeType?.toLowerCase();

    if (mimeType?.startsWith("image/")) {
        return true;
    }

    return /\.(jpe?g|png|webp)$/i.test(file.fileName);
}

function formatFileSize(size: number) {
    if (size < 1024) return `${size} B`;

    const kilobytes = size / 1024;
    if (kilobytes < 1024) return `${kilobytes.toFixed(1)} KB`;

    return `${(kilobytes / 1024).toFixed(1)} MB`;
}
